import { useMemo } from 'react';

import { useCurrentUser } from '@bizzabo/entities';

import useScheduledMeetings from '../hooks/useScheduledMeetings';
import { useMeetings } from '../MeetingsTool/MeetingsToolContext';
import { ScheduledMeeting } from '../types';

export enum MeetingsHttpKey {
    Meetings = 'MEETINGS',
    MeetingsTotal = 'MEETINGS_TOTAL',
    MeetingsGroups = 'MEETING_GROUPS',
    MeetingsUsers = 'MEETING_USERS',
}

const useMeetingsData = () => {
    const { eventId } = useMeetings();
    const { data: currentUser } = useCurrentUser(eventId);

    const {
        data: scheduledMeetings,
        isFetching: isFetchingMeetings,
        isLoading: isLoadingMeetings,
    } = useScheduledMeetings({
        key: MeetingsHttpKey.Meetings,
        eventId,
        size: 100,
        page: 0,
    });

    const shouldCallWithToatlElements =
        scheduledMeetings?.content.length < scheduledMeetings?.page.totalElements;

    const {
        data: scheduledMeetingsWithTotalElements,
        isLoading: isLoadingTotal,
        isFetching: isFetchingTotal,
        isFetched: isFetchedWithTotalElements,
    } = useScheduledMeetings({
        key: MeetingsHttpKey.MeetingsTotal,
        eventId,
        size: scheduledMeetings?.page.totalElements,
        page: 0,
        options: { enabled: shouldCallWithToatlElements },
    });

    const getUsersIds = (meetings: ScheduledMeeting[]) => {
        const usersIds = meetings
            .flatMap(meeting => meeting.participants)
            .filter(participant => participant.userProfileId !== currentUser?.userProfileId)
            .map(participant => participant.userProfileId);

        return [...new Set(usersIds)];
    };

    const meetings = useMemo(() => {
        return isFetchedWithTotalElements
            ? scheduledMeetingsWithTotalElements?.content
            : scheduledMeetings?.content;
    }, [isFetchedWithTotalElements, scheduledMeetings?.content]);

    const usersProfileIds = useMemo(() => {
        if (!meetings) {
            return null;
        }
        return getUsersIds(meetings);
    }, [meetings?.length]);

    return {
        meetings,
        usersProfileIds,
        isLoading: isLoadingMeetings || isLoadingTotal,
        isFetching: isFetchingMeetings || isFetchingTotal,
    };
};

export default useMeetingsData;
